// The store admin's own notifications, e.g. "New order received"
// (uniquegiftapi#617 /seller/notifications). They need the seller session
// (X-Seller-Token), which the shared api instance sends.
//
// The bell's state lives here, not in NavigationBar: every page mounts its own
// NavigationBar, and the bell must keep what it has seen and marked read
// across page changes (PR #289 review L-2, L-3, L-5).
import api from '../api/axiosInstance';
import { getSellerToken } from '../utils/sellerSession';

// How many unread notifications the bell lists
export const BELL_LIMIT = 20;

// How often the bell checks for new ones, while the page is visible
export const POLL_INTERVAL_MS = 60000;

// How long a page change may leave the bell without a NavigationBar before polling stops
const STOP_GRACE_MS = 5000;

const resultOf = (data) => (data && data.status === 'success' ? data.result : null);

/** The store's unread notifications, newest first. */
export const fetchUnreadNotifications = async (limit = BELL_LIMIT) => {
  const { data } = await api.get('/seller/notifications', { params: { unread: 'true', limit } });
  const result = resultOf(data);
  return Array.isArray(result) ? result : [];
};

/** How many of the store's notifications are unread (may be more than the bell lists). */
export const fetchUnreadCount = async () => {
  const { data } = await api.get('/seller/notifications/unread-count');
  const result = resultOf(data);
  return Number(result && result.unread_count) || 0;
};

/** Marks these notifications read on the server (the store's own only). */
export const markNotificationsRead = (ids) =>
  api.post('/seller/notifications/read', { notification_ids: ids });

/**
 * Marks the store's notifications read on the server, up to `before`: one
 * that arrived after the newest the seller saw stays unread (review M-1).
 */
export const markAllNotificationsRead = (before) =>
  api.post('/seller/notifications/read', before ? { all: true, before } : { all: true });

/**
 * Where clicking a notification goes: a new order opens that order's page in
 * Order Management, where the parcel service and tracking details are added.
 * Null when it has no order to open.
 */
export const notificationPath = (notification) => {
  const eventId = notification && notification.data && notification.data.event_id;
  if (!eventId) return null;
  return `/ordermanagement_view?id=${encodeURIComponent(eventId)}`;
};

/** "just now", "5 min ago", "3 h ago", "2 d ago" */
export const timeAgo = (date, now = Date.now()) => {
  const minutes = Math.floor((now - new Date(date).getTime()) / 60000);
  if (!(minutes >= 1)) return 'just now';
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} h ago`;
  return `${Math.floor(hours / 24)} d ago`;
};

// The seller token's payload, or null
const sellerTokenPayload = () => {
  const token = getSellerToken();
  if (!token) return null;
  try {
    return JSON.parse(window.atob(token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')));
  } catch (e) {
    return null;
  }
};

/**
 * Whether the seller token is there and not expired. The bell checks only
 * then: a 401 on a background check would send the seller to log in again
 * from whatever page they are on (axiosInstance).
 */
export const sellerSessionActive = (now = Date.now()) => {
  const payload = sellerTokenPayload();
  return !!payload && (!payload.exp || payload.exp * 1000 > now);
};

// ---------------------------------------------------------------------------
// The bell's shared state
// ---------------------------------------------------------------------------

const newStore = () => ({
  owner: null, // the seller (token sub) this state belongs to
  notifications: [],
  unreadCount: 0,
  seenIds: null, // ids already shown; null until the first check
  readIds: new Set(), // marked read here: hidden even if a check started earlier says unread
  readBefore: null, // "mark all" up to this time: same
  lastCheck: 0,
  intervalId: null,
  stopTimer: null,
  listeners: new Set(),
});
let store = newStore();

const snapshot = (added = []) => ({ notifications: store.notifications, unreadCount: store.unreadCount, added });
const emit = (added) => store.listeners.forEach((listener) => listener(snapshot(added)));

// Another seller logged in (same tab): nothing of the last one's carries over
const ensureOwner = () => {
  const payload = sellerTokenPayload();
  const owner = payload ? String(payload.sub || '') : null;
  if (owner !== store.owner) {
    const { listeners, intervalId, stopTimer } = store;
    store = { ...newStore(), owner, listeners, intervalId, stopTimer };
  }
};

const isMarkedRead = (notification) => store.readIds.has(notification._id)
  || (store.readBefore !== null && new Date(notification.created_date).getTime() <= store.readBefore);

/** One check of the server; the bell's listeners get the result. */
export const checkNotifications = async () => {
  if (!sellerSessionActive()) return;
  ensureOwner();
  const owner = store.owner;
  store.lastCheck = Date.now();
  try {
    const [listed, serverCount] = await Promise.all([fetchUnreadNotifications(), fetchUnreadCount()]);
    if (owner !== store.owner) return; // another seller since
    // What was marked read here is gone, even if this check started first
    const notifications = listed.filter((n) => !isMarkedRead(n));
    const unreadCount = Math.max(0, serverCount - (listed.length - notifications.length));
    // New = not shown before (by id, not by count), and never on the first check
    const added = store.seenIds ? notifications.filter((n) => !store.seenIds.has(n._id)) : [];
    store.seenIds = new Set([...(store.seenIds || []), ...notifications.map((n) => n._id)]);
    store.notifications = notifications;
    store.unreadCount = unreadCount;
    emit(added);
  } catch (error) {
    // An expired session or a network error: try again at the next check
    console.error('[sellerNotificationService] Error fetching notifications:', error);
  }
};

/**
 * The bell: listener({ notifications, unreadCount, added }) now with what is
 * known, and after each check (added: the ones not shown before). Polls while
 * the page is visible; a page change keeps the state and doesn't check again
 * before the interval is up. Returns the unsubscribe function.
 */
export const subscribeToNotifications = (listener, interval = POLL_INTERVAL_MS) => {
  ensureOwner();
  store.listeners.add(listener);
  clearTimeout(store.stopTimer);
  store.stopTimer = null;
  if (store.seenIds) listener(snapshot());
  if (!store.intervalId) {
    store.intervalId = setInterval(() => {
      if (document.visibilityState === 'visible') checkNotifications();
    }, interval);
  }
  if (Date.now() - store.lastCheck >= interval) checkNotifications();
  return () => {
    store.listeners.delete(listener);
    if (store.listeners.size) return;
    store.stopTimer = setTimeout(() => {
      clearInterval(store.intervalId);
      store.intervalId = null;
    }, STOP_GRACE_MS);
  };
};

// The server refused a mark: the bell shows what the server says again
// (round 2 N-3), instead of hiding it until the page reloads
const undoMark = (undo) => (error) => {
  undo();
  checkNotifications();
  throw error;
};

/**
 * Marks one notification read: gone from the bell at once, then on the
 * server. Resolves when the server has it (or rejects, and the bell shows it
 * again), so the caller can wait before leaving the page.
 */
export const readNotification = (notification) => {
  const id = notification._id;
  store.readIds.add(id);
  const listed = store.notifications.some((n) => n._id === id);
  store.notifications = store.notifications.filter((n) => n._id !== id);
  if (listed) store.unreadCount = Math.max(0, store.unreadCount - 1);
  emit();
  const readIds = store.readIds;
  return markNotificationsRead([id]).catch(undoMark(() => readIds.delete(id)));
};

/**
 * Marks read everything up to the newest notification the seller sees. One
 * that arrived after it stays unread and shows at the next check (review M-1).
 */
export const readAllNotifications = () => {
  const newest = store.notifications.reduce((max, n) => {
    const t = new Date(n.created_date).getTime();
    return Number.isNaN(t) ? max : Math.max(max, t);
  }, -Infinity);
  if (newest === -Infinity) return Promise.resolve();
  const previous = store.readBefore;
  const marked = store;
  store.readBefore = Math.max(store.readBefore || 0, newest);
  store.notifications = [];
  // Unread beyond what the bell listed may be older (marked too) or newer (kept): the next check says
  store.unreadCount = 0;
  emit();
  return markAllNotificationsRead(new Date(newest).toISOString()).catch(undoMark(() => {
    if (marked === store) store.readBefore = previous;
  }));
};

/** For tests: forget everything and stop polling. */
export const resetNotificationStore = () => {
  clearInterval(store.intervalId);
  clearTimeout(store.stopTimer);
  store = newStore();
};
